import React from 'react'
import clsx from 'clsx'

type PaginationProps = {
  totalItems: number // e.g. 30
  pageSize: number // e.g. 10
  currentPage: number // 1-based
  onPageChange: (page: number) => void
  labelSuffix?: string // e.g. "pegawai"
  siblingCount?: number // how many page neighbors to show
  className?: string
}

function range(start: number, end: number) {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

export const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  pageSize,
  currentPage,
  onPageChange,
  siblingCount = 1,
  className,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const clampedPage = Math.min(Math.max(currentPage, 1), totalPages)

  const start = totalItems === 0 ? 0 : (clampedPage - 1) * pageSize + 1
  const end = Math.min(clampedPage * pageSize, totalItems)

  // Build page list with optional ellipses
  const firstPage = 1
  const lastPage = totalPages
  const leftSibling = Math.max(clampedPage - siblingCount, firstPage)
  const rightSibling = Math.min(clampedPage + siblingCount, lastPage)

  const showLeftEllipsis = leftSibling > firstPage + 1
  const showRightEllipsis = rightSibling < lastPage - 1

  const middle = range(leftSibling, rightSibling)
  const pages: (number | '…')[] = []

  if (totalPages > 0) {
    pages.push(firstPage)
    if (showLeftEllipsis) pages.push('…')
    for (const p of middle) {
      if (p !== firstPage && p !== lastPage) pages.push(p)
    }
    if (showRightEllipsis) pages.push('…')
    if (lastPage !== firstPage) pages.push(lastPage)
  }

  const navBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'
  const pill =
    'inline-flex h-9 min-w-9 items-center justify-center rounded-[4px] border text-sm'
  const pillActive = 'border-blue-600 bg-blue-600 text-white'
  const pillIdle = 'border-gray-300 bg-white hover:bg-gray-50 hover:text-white'

  return (
    <div className={clsx('flex items-center justify-between gap-4', className)}>
      <div className="text-sm text-gray-700">
        Menampilkan <span className="font-semibold">{start}</span>–
        <span className="font-semibold">{end}</span> dari{' '}
        <span className="font-semibold">{totalItems}</span> nama
      </div>

      <nav
        className="flex items-center gap-2"
        role="navigation"
        aria-label="Pagination"
      >
        <button
          className={navBtn}
          onClick={() => onPageChange(clampedPage - 1)}
          disabled={clampedPage <= 1}
          aria-label="Sebelumnya"
        >
          ‹
        </button>

        {pages.map((p, i) =>
          p === '…' ? (
            <span
              key={`dots-${i}`}
              className="px-2 text-sm text-gray-500 select-none"
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={p}
              className={`${pill} ${p === clampedPage ? pillActive : pillIdle}`}
              aria-current={p === clampedPage ? 'page' : undefined}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          )
        )}

        <button
          className={navBtn}
          onClick={() => onPageChange(clampedPage + 1)}
          disabled={clampedPage >= totalPages}
          aria-label="Berikutnya"
        >
          ›
        </button>
      </nav>
    </div>
  )
}
